import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { EnvelopeSimple, Info, CaretDown, CaretUp, Paperclip, ArrowsOut, ArrowsIn } from '@phosphor-icons/react';
import BuildingRecipientsPicker from './BuildingRecipientsPicker';
import RichEmailEditor from './RichEmailEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_TEMPLATES = {
  invoice: {
    subject: 'Common Expenses – {invoice_number} – {company_name}',
    body: 'Dear {owner_name},\n\nPlease find attached the invoice {invoice_number} regarding the common expenses for apartment {apartment_number} for the month of {month}.\n\nBest regards,\n{company_name}',
  },
  payment: {
    subject: 'Payment Receipt – {apartment_number} – {company_name}',
    body: 'Dear {owner_name},\n\nPlease find attached the payment receipt for apartment {apartment_number}.\n\nBest regards,\n{company_name}',
  },
  statement: {
    subject: 'Common Expenses Statement – {apartment_number} – {company_name}',
    body: 'Dear {owner_name},\n\nPlease find attached your common expenses statement for apartment {apartment_number} for the period from {start_date} to {end_date}.\n\nBest regards,\n{company_name}',
  },
  meeting: {
    subject: 'General Meeting Notice – {building_name} – {meeting_date} – {company_name}',
    body: 'Dear Owners,\n\nPlease find attached the minutes of the {meeting_type} for {building_name} held on {meeting_date}.\n\nBest regards,\n{company_name}',
  },
};

function replacePlaceholders(text, fields) {
  if (!text) return '';
  const month = fields.month || fields.period || '';
  let result = text
    .replace(/\{apartment_number\}/g, fields.apartment_number || '')
    .replace(/\{invoice_number\}/g, fields.invoice_number || '')
    .replace(/\{company_name\}/g, fields.company_name || '')
    .replace(/\{building_name\}/g, fields.building_name || '')
    .replace(/\{owner_name\}/g, fields.owner_name || '')
    .replace(/\{start_date\}/g, fields.start_date || '')
    .replace(/\{end_date\}/g, fields.end_date || '')
    .replace(/\{month\}/g, month)
    .replace(/\{period\}/g, month)
    .replace(/\{meeting_date\}/g, fields.meeting_date || '')
    .replace(/\{meeting_type\}/g, fields.meeting_type || '');
  result = result
    .replace(/\[APARTMENT NUMBER\/NAME\]/gi, fields.apartment_number || '')
    .replace(/\[ΑΡΙΘΜΟΣ\/ΟΝΟΜΑ\]/gi, fields.apartment_number || '')
    .replace(/\[START DATE\]/gi, fields.start_date || '')
    .replace(/\[ΑΠΟ ΗΜΕΡΟΜΗΝΙΑ\]/gi, fields.start_date || '')
    .replace(/\[END DATE\]/gi, fields.end_date || '')
    .replace(/\[ΕΩΣ ΗΜΕΡΟΜΗΝΙΑ\]/gi, fields.end_date || '')
    .replace(/\[YOUR NAME \/ COMPANY\]/gi, fields.company_name || '')
    .replace(/\[ΤΟ ΟΝΟΜΑ ΣΑΣ \/ ΕΤΑΙΡΕΙΑ\]/gi, fields.company_name || '')
    .replace(/\[MONTH\]/gi, month)
    .replace(/\[ΜΗΝΑΣ\]/gi, month);
  return result;
}

/**
 * Phase 675 — Convert a plain-text email template (stored in Settings →
 * Email Templates as a multi-line string with `\n` line breaks) into HTML
 * so Quill renders the paragraph structure exactly as the user authored it.
 *
 * Why this is needed:
 *  • Templates are stored in `Textarea` form fields → saved as plain text.
 *  • Quill receives its `value` as HTML. Raw `\n` in HTML is whitespace and
 *    collapses into one paragraph, which is exactly the bug the user reported.
 *  • We detect already-HTML bodies (the new RichEmailEditor saves these)
 *    and pass them through untouched so we never double-escape.
 *
 * Conversion rules for plain text:
 *  • HTML-escape `&`, `<`, `>` so any angle brackets in the template
 *    render as literal characters.
 *  • Wrap each line in a `<p>` so Quill keeps it as a separate paragraph.
 *  • A blank line in the template becomes an empty `<p><br></p>` paragraph,
 *    which is how Quill represents intentional vertical whitespace.
 */
function templateBodyToHtml(text) {
  if (!text) return '';
  // Heuristic — if the template already contains common HTML block/inline
  // tags it's a rich-text body and we must NOT touch it.
  if (/<\/?(?:p|div|br|ul|ol|li|h[1-6]|strong|em|span|a|img|blockquote)\b/i.test(text)) {
    return text;
  }
  const escape = (s) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Normalise CRLF / CR to LF, then split per-line so blank lines survive.
  const lines = String(text).replace(/\r\n?/g, '\n').split('\n');
  return lines
    .map((ln) => (ln.length === 0 ? '<p><br></p>' : `<p>${escape(ln)}</p>`))
    .join('');
}

export default function SendEmailDialog({
  open,
  onOpenChange,
  pdfUrl,
  filename,
  defaultEmail,
  templateType = 'invoice',
  dynamicFields = {},
  apartmentId = '',
  buildingId = '',
  docType = '',
  relatedId = '',
  onSent,
  // Phase 208 — apartment recipient breakdown for quick CC checkbox.
  // Shape: { primary, owner_email, tenant_email, extra_emails, responsible_role }
  apartmentRecipients = null,
}) {
  const { t } = useLanguage();
  const [sending, setSending] = useState(false);
  // Phase 658 — Maximize toggle. Default size is already much larger than
  // before; clicking the icon switches between "wide" and full-screen.
  const [maximized, setMaximized] = useState(false);
  const [form, setForm] = useState({ to_email: '', cc: '', bcc: '', subject: '', message: '' });
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  // Phase 359 — Apartments recipients picker (shared with BuildingEmailsTab).
  // Only shown when `buildingId` is provided so the picker can call the
  // building-scoped recipients endpoint.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('to');
  const openPicker = (target) => { setPickerTarget(target); setPickerOpen(true); };
  const onPickerConfirm = (emails) => {
    const joined = Array.from(new Set(emails.filter(Boolean))).join(', ');
    setForm(prev => ({ ...prev, [pickerTarget === 'to' ? 'to_email' : pickerTarget]: joined }));
    if (pickerTarget !== 'to') setShowCcBcc(true);
  };
  // Quick CC: when checked, the "other" party (owner if responsible=tenant,
  // tenant if responsible=owner) gets added to the CC field.
  const [ccOtherParty, setCcOtherParty] = useState(false);
  const otherPartyEmail = (() => {
    if (!apartmentRecipients) return '';
    if (apartmentRecipients.responsible_role === 'tenant') return apartmentRecipients.owner_email || '';
    return apartmentRecipients.tenant_email || '';
  })();
  const otherPartyLabel = apartmentRecipients?.responsible_role === 'tenant' ? 'Owner' : 'Tenant';

  const applyTemplate = useCallback((settings, fields) => {
    const typeKey = templateType;
    const fallback = DEFAULT_TEMPLATES[typeKey] || DEFAULT_TEMPLATES.invoice;
    const subjectTemplate = settings[`${typeKey}_subject_template`] || fallback.subject;
    const bodyTemplate = settings[`${typeKey}_body_template`] || fallback.body;
    // Phase 675 — Convert plain-text bodies (with `\n` line breaks) to HTML
    // BEFORE handing the value to Quill so paragraph structure / blank
    // lines are preserved exactly as authored in Settings → Templates.
    const rawBody = replacePlaceholders(bodyTemplate, fields);
    return {
      subject: replacePlaceholders(subjectTemplate, fields),
      message: templateBodyToHtml(rawBody),
    };
  }, [templateType]);

  useEffect(() => {
    if (!open) {
      setTemplateLoaded(false);
      return;
    }

    const loadTemplates = async () => {
      let companyName = dynamicFields.company_name || '';
      if (!companyName) {
        try {
          const cpRes = await axios.get(`${API}/company-profile`);
          companyName = cpRes.data?.company_name || '';
        } catch { /* ignore */ }
      }
      const enrichedFields = { ...dynamicFields, company_name: companyName };
      try {
        const res = await axios.get(`${API}/email-settings`);
        const settings = res.data || {};
        const { subject, message } = applyTemplate(settings, enrichedFields);
        setForm(prev => ({ to_email: defaultEmail || '', cc: prev.cc || '', bcc: prev.bcc || '', subject, message }));
      } catch {
        const { subject, message } = applyTemplate({}, enrichedFields);
        setForm(prev => ({ to_email: defaultEmail || '', cc: prev.cc || '', bcc: prev.bcc || '', subject, message }));
      }
      setTemplateLoaded(true);
    };
    loadTemplates();
  }, [open, defaultEmail, dynamicFields, applyTemplate]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.to_email) {
      toast.error(t('recipientEmail') + ' required');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('cleanpro_token') || '';
      // Merge inline CC field with auto-CC of "other party" if checkbox is checked.
      const inlineCc = (form.cc || '').trim();
      const ccList = [];
      if (inlineCc) ccList.push(inlineCc);
      if (ccOtherParty && otherPartyEmail && otherPartyEmail !== form.to_email) {
        if (!ccList.includes(otherPartyEmail)) ccList.push(otherPartyEmail);
      }
      const finalCc = ccList.join(', ');
      const resp = await axios.post(`${API}/send-document-email`, {
        to_email: form.to_email,
        cc: finalCc,
        bcc: form.bcc || '',
        subject: form.subject || filename || 'Document',
        message: form.message,
        pdf_url: pdfUrl,
        filename: filename || 'document.pdf',
        token: token,
        apartment_id: apartmentId,
        building_id: buildingId,
        doc_type: docType || templateType,
        related_id: relatedId || '',
      });
      // Phase 209p — Surface PDF-attachment fallback to the user. The email
      // was sent, but if the PDF couldn't be generated (e.g. timeout or
      // permission), the recipient got a plain email — let the sender know
      // so they can retry sending the PDF separately.
      if (resp?.data?.attachment_included === false) {
        toast.warning(
          'Το email στάλθηκε, αλλά το PDF δεν επισυνάφθηκε λόγω σφάλματος δημιουργίας. Δοκιμάστε να στείλετε ξανά.'
        );
      } else {
        toast.success(t('emailSentSuccess'));
      }
      if (onSent) onSent();
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('not configured')) {
        toast.error(t('emailNotConfigured'));
      } else {
        toast.error(detail || t('errorOccurred'));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={maximized
          ? "p-0 rounded-none flex flex-col w-screen h-screen max-w-none sm:!max-w-none"
          : "rounded-none p-0 flex flex-col w-[95vw] max-w-[1200px] sm:!max-w-[1200px] h-[88vh] max-h-[88vh] resize overflow-hidden"}
        data-testid="send-email-dialog"
      >
        <DialogHeader className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-zinc-200">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              <EnvelopeSimple size={20} />
              {t('sendByEmail')}
            </DialogTitle>
            <button
              type="button"
              onClick={() => setMaximized(m => !m)}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-none"
              title={maximized ? (t('restore') || 'Restore') : (t('maximize') || 'Maximize')}
              data-testid="email-maximize-btn"
            >
              {maximized ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
            </button>
          </div>
        </DialogHeader>
        {!templateLoaded ? (
          <div className="py-8 text-center text-sm text-zinc-400">{t('loading')}</div>
        ) : (
          <form onSubmit={handleSend} className="flex flex-col flex-1 min-h-0 gap-3 px-5 pt-3 pb-4">
            {/* Recipients Section */}
            <div className="flex-shrink-0 space-y-2">
              {/* To */}
              <div className="flex items-center gap-2">
                <Label className="text-xs w-10 text-right text-zinc-500 flex-shrink-0">{t('to')}</Label>
                <Input
                  type="text"
                  value={form.to_email}
                  onChange={e => setForm({ ...form, to_email: e.target.value })}
                  className="rounded-none text-sm flex-1"
                  required
                  data-testid="email-recipient"
                  placeholder={t('recipientEmailPlaceholder')}
                />
                {buildingId && (
                  <button
                    type="button"
                    onClick={() => openPicker('to')}
                    className="text-[11px] underline text-zinc-500 hover:text-zinc-900 px-1 flex-shrink-0"
                    data-testid="email-pick-to"
                  >
                    {t('pickFromApartments') || 'Pick'}
                  </button>
                )}
                {!showCcBcc && (
                  <button
                    type="button"
                    onClick={() => setShowCcBcc(true)}
                    className="text-[11px] text-zinc-400 hover:text-zinc-700 px-2 py-1 flex-shrink-0 transition-colors"
                    data-testid="toggle-cc-bcc"
                  >
                    CC / BCC
                  </button>
                )}
              </div>

              {/* Phase 208 — Quick CC the "other party" (owner/tenant) */}
              {apartmentRecipients && otherPartyEmail && (
                <div className="flex items-center gap-2 pl-12">
                  <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer" data-testid="cc-other-party-toggle">
                    <input
                      type="checkbox"
                      checked={ccOtherParty}
                      onChange={(e) => setCcOtherParty(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span>CC τον {otherPartyLabel} <span className="font-mono text-zinc-400">({otherPartyEmail})</span></span>
                  </label>
                </div>
              )}

              {/* CC / BCC (collapsible) */}
              {showCcBcc && (
                <>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs w-10 text-right text-zinc-500 flex-shrink-0">CC</Label>
                    <Input
                      type="text"
                      value={form.cc}
                      onChange={e => setForm({ ...form, cc: e.target.value })}
                      className="rounded-none text-sm flex-1"
                      data-testid="email-cc"
                      placeholder={t('ccPlaceholder')}
                    />
                    {buildingId && (
                      <button
                        type="button"
                        onClick={() => openPicker('cc')}
                        className="text-[11px] underline text-zinc-500 hover:text-zinc-900 px-1 flex-shrink-0"
                        data-testid="email-pick-cc"
                      >
                        {t('pickFromApartments') || 'Pick'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs w-10 text-right text-zinc-500 flex-shrink-0">BCC</Label>
                    <Input
                      type="text"
                      value={form.bcc}
                      onChange={e => setForm({ ...form, bcc: e.target.value })}
                      className="rounded-none text-sm flex-1"
                      data-testid="email-bcc"
                      placeholder={t('bccPlaceholder')}
                    />
                    {buildingId && (
                      <button
                        type="button"
                        onClick={() => openPicker('bcc')}
                        className="text-[11px] underline text-zinc-500 hover:text-zinc-900 px-1 flex-shrink-0"
                        data-testid="email-pick-bcc"
                      >
                        {t('pickFromApartments') || 'Pick'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowCcBcc(false)}
                      className="text-[11px] text-zinc-400 hover:text-zinc-700 px-2 py-1 flex-shrink-0 transition-colors"
                      data-testid="hide-cc-bcc"
                    >
                      <CaretUp size={12} />
                    </button>
                  </div>
                </>
              )}

              {/* Subject */}
              <div className="flex items-center gap-2">
                <Label className="text-xs w-10 text-right text-zinc-500 flex-shrink-0">{t('subject')}</Label>
                <Input
                  value={form.subject}
                  onChange={e => setForm({ ...form, subject: e.target.value })}
                  className="rounded-none text-sm flex-1"
                  data-testid="email-subject"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-200 flex-shrink-0" />

            {/* Message Body — Phase 658: Rich-text editor (Quill) replaces
                the plain textarea so users get bold/italic/underline/lists/
                alignment/links/undo-redo. Body is saved as HTML and sent
                exactly as composed (no plain-text downgrade). */}
            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="email-message-wrap">
              <RichEmailEditor
                value={form.message}
                onChange={(html) => setForm(prev => ({ ...prev, message: html }))}
                minHeight={maximized ? "calc(100vh - 320px)" : "320px"}
                testId="email-message-editor"
              />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-between border-t border-zinc-200 pt-3">
              <div className="flex items-center gap-2 text-zinc-400">
                <Paperclip size={14} />
                <span className="text-[11px] truncate max-w-[300px]">{filename}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="rounded-none"
                  disabled={sending}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-zinc-950 rounded-none"
                  disabled={sending}
                  data-testid="send-email-btn"
                >
                  {sending ? t('sending') : t('sendByEmail')}
                </Button>
              </div>
            </div>
          </form>
        )}
        {/* Phase 359 — Apartments recipients picker. Only rendered when a
            building context is available. */}
        {buildingId && (
          <BuildingRecipientsPicker
            buildingId={buildingId}
            open={pickerOpen}
            target={pickerTarget}
            initialEmails={pickerTarget === 'to' ? form.to_email : pickerTarget === 'cc' ? form.cc : form.bcc}
            onClose={() => setPickerOpen(false)}
            onConfirm={onPickerConfirm}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
